import { useMutation } from '@apollo/client';
import EditActionButton from '@components/EditActionButton';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_TALENT, UPDATE_PREFERED_SALARY_TALENT } from '@gql/talents';
import { Box, FormControlLabel, Switch, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';

import { SectionCard } from './SectionCard';
import TalentSalaryForm from './TalentSalaryForm';
import { SectionTitle } from './SectionTitle';

interface TalentSalaryCardProps {
    talentId: string;
    talentName?: string;
    currentSalary?: number | null;
    expectedSalary?: number | null;
}

const formatSalary = (salary?: number | null) => {
    if (salary === null || salary === undefined) {
        return 'Non renseigné';
    }

    return `${salary.toLocaleString('fr-FR')} €`;
};

const TalentSalaryCard = ({ talentId, talentName, currentSalary, expectedSalary }: TalentSalaryCardProps) => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();

    const [isEditMode, setIsEditMode] = useState(false);
    const [showValues, setShowValues] = useState(false);

    const [updateSalary, { loading: isSubmitting }] = useMutation(UPDATE_PREFERED_SALARY_TALENT);

    const displayName = useMemo(() => {
        if (!talentName) {
            return 'du talent';
        }

        const [firstName] = talentName.trim().split(' ');
        return `de ${firstName}`;
    }, [talentName]);

    const handleSubmitSalary = async (payload: { currentSalary: number | null; expectedSalary: number | null }) => {
        try {
            await updateSalary({
                variables: {
                    talentId,
                    payload,
                },
                refetchQueries: [
                    {
                        query: GET_TALENT,
                        variables: { talentId },
                    },
                ],
            });

            showSnackbar('Salaire mis a jour avec succes', 'success');
            setIsEditMode(false);
        } catch (_error) {
            showSnackbar('Erreur lors de la mise a jour du salaire', 'error');
        }
    };

    return (
        <SectionCard
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '12px 16px',
                flex: 1,
                background: theme.palette.dashboard.main,
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SectionTitle title={`Salaire ${displayName}`} />
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                            <Switch
                                checked={showValues}
                                onChange={(_, checked) => setShowValues(checked)}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: theme.palette.neutralWhite.main,
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: theme.palette.primary.main,
                                        opacity: 1,
                                    },
                                }}
                            />
                        }
                        label=""
                    />
                </Box>

                {!isEditMode && (
                    <EditActionButton
                        onClick={() => setIsEditMode(true)}
                    />
                )}
            </Box>

            {isEditMode ? (
                <TalentSalaryForm
                    initialCurrentSalary={currentSalary}
                    initialExpectedSalary={expectedSalary}
                    loading={isSubmitting}
                    onCancel={() => setIsEditMode(false)}
                    onSubmit={handleSubmitSalary}
                />
            ) : (
                <Box
                    sx={{
                        borderRadius: '16px',
                        px: '12px',
                        py: '16px',
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'space-between',
                        gap: '16px',
                        background: theme.palette.disableFormBlack.main,
                    }}>
                    {[
                        {
                            title: 'Salaire actuel',
                            value: showValues ? formatSalary(currentSalary) : '**** €',
                        },
                        {
                            title: 'Salaire attendu',
                            value: showValues ? formatSalary(expectedSalary) : '**** €',
                        },
                    ].map(({ title, value }, index) => (
                        <Box
                            key={title}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                flex: 1,
                                pr: index === 0 ? '16px' : 0,
                                borderRight: index === 0 ? `1px dashed ${theme.palette.text.secondary}` : 'none',
                            }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                }}>
                                {title}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '20px',
                                    lineHeight: '28px',
                                    fontWeight: 700,
                                    color: theme.palette.neutralWhite.main,
                                }}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </SectionCard>
    );
};

export default TalentSalaryCard;
